import React from "react";
import "./App.css";
import { Login } from "./components/login/Login";
import "bootstrap/dist/css/bootstrap.min.css";
import {
	AppState,
	BoardDataMap,
	ErrHistoryProps,
	HomeProps,
	LayoutProps,
	LinkFormProps,
	LinkTableProps,
	LoginProps,
	RepoDataMap,
} from "./types/appTypes";
import { Navigate, Route, Routes } from "react-router-dom";
import Layout from "./components/layout/Layout";
import LinkTable from "./components/linkTable/LinkTable";
import LinkForm from "./components/linkForm/LinkForm";
import Home from "./components/Home";
import { KanbanizeBoard, KanbanizeCredentials, KanbanizeTokenObj, RepoLink, Repository } from "./types/kanbanizeTypes";
import { updateDomain, updateCurrentConfig, updateUserToken } from "./utils/store";
import { Container } from "react-bootstrap";
import { doPost, doPut, getUserNameByCode, refreshBoards, refreshRepolinksAndRepos } from "./utils/utils";
import ErrorHistory from "./components/errorHistory/ErrorHistory";
import { ThemeProvider, createTheme } from "@mui/material";

export default class App extends React.Component<any, AppState> {
	constructor(props: any) {
		super(props);
		const appState: AppState = {
			integrationApiUrl: process.env.REACT_APP_INTEGRATION_URL as string,
			kanbanizeDomain: undefined,
			kanbanizeApiKey: undefined,
			currentlyEditingRepoName: undefined,
			refresh_repos_on_org_change: true,
			userToken: undefined,
			boards: [],
			listConfig: [],
			repositories: [],
			currentConfig: undefined,
			generalError: false,
			userName: undefined,
			boardDataMap: {},
			repoDataMap: {},
			corporateUserOrgs: [],
			currentUserOrg: "",
			current_component: "",
			refreshing_repos_org_change: false,
		};

		this.state = appState;
		this.setUserToken.bind(this);
		this.logOut.bind(this);
		this.setKanbanizeDomain.bind(this);
		this.setListConfig.bind(this);
		this.getLoginProps.bind(this);
		this.getLinkTableProps.bind(this);
		this.getLinkFormProps.bind(this);
		this.getUserName.bind(this);
	}

	componentDidMount() {
		document.title = `Businessmap to ${
			process.env.REACT_APP_INTEGRATION_SERVICE ? process.env.REACT_APP_INTEGRATION_SERVICE : ""
		}${process.env.REACT_APP_INTEGRATION_VERSION ? " (" + process.env.REACT_APP_INTEGRATION_VERSION + ")" : ""} integration`;
		const search = window.location.search;
		this.getUserName(search);
	}

	setCorporateUserOrgs = (corporateUserOrgs: string[]) => {
		this.setState({ corporateUserOrgs, currentUserOrg: this.state.currentUserOrg || corporateUserOrgs[0] });
	};

	changeCorporateUserOrg = async (user_org: string) => {
		console.log("changeCorporateUserOrg: init");
		// Call backend to update current user organization.
		await doPut<{ current_org: string }>(`${this.state.integrationApiUrl}/api/login/corporate/current_org/${user_org}`, {});
		this.setState({ currentUserOrg: user_org, listConfig: [], refreshing_repos_org_change: true }, async () => {
			const boards = await refreshBoards(this.state.integrationApiUrl, this.state.userToken);
			const repolinks_repos = await refreshRepolinksAndRepos(
				this.state.integrationApiUrl,
				this.state.kanbanizeDomain || "",
				this.state.userToken,
			);
			this.setState({
				repositories: repolinks_repos.allRepos,
				listConfig: repolinks_repos.savedConfigs,
				boards,
				refreshing_repos_org_change: false,
			});
		});
	};

	setCurrentComponent = (component: string) => {
		this.setState({ current_component: component });
	};

	addBoardDataMap = (board: string, dataMap: BoardDataMap["key"]) => {
		let currentState = this.state.boardDataMap;
		if (!currentState) currentState = {};
		currentState[board] = dataMap;
		this.setState({ boardDataMap: currentState });
	};

	addRepoDataMap = (repo: string, dataMap: RepoDataMap["key"]) => {
		let currentState = this.state.repoDataMap;
		if (!currentState) currentState = {};
		currentState[repo] = dataMap;
		this.setState({ repoDataMap: currentState });
	};

	setListConfig = (listConfig: RepoLink[]) => {
		this.setState({ listConfig });
	};

	setRepositories = (repositories: Repository[]) => {
		this.setState({ repositories });
	};

	setBoards = (boards: KanbanizeBoard[]) => {
		this.setState({ boards });
	};

	getUserName = async (search: string) => {
		if (process.env.REACT_APP_INTEGRATION_SERVICE !== "GitHub") {
			const username = new URLSearchParams(search).get("username");
			if (username) {
				this.setState({ userName: username });
			}
		} else {
			const code = new URLSearchParams(search).get("code");
			const state = new URLSearchParams(search).get("state");
			if (code) {
				const username = await getUserNameByCode(this.state.integrationApiUrl as string, code, state);
				this.setState({ userName: username });
			}
		}
	};

	setUserToken = (token: string) => {
		this.setState({ userToken: token }, () => {
			updateUserToken(token);
		});
	};

	logOut = () => {
		this.setState({ userToken: undefined, userName: undefined, corporateUserOrgs: [] });
	};

	setKanbanizeDomain = (domain: string) => {
		this.setState({ kanbanizeDomain: domain }, () => {
			updateDomain(domain);
		});
	};

	setKanbanizeApiKey = (apiKey: string) => {
		this.setState({ kanbanizeApiKey: apiKey });
	};

	setCurrentConfig = (currentConfig: RepoLink) => {
		this.setState({ currentlyEditingRepoName: currentConfig.repo_name }); //TODO: add to persistent redux like below.
		this.setState({ currentConfig: currentConfig }, () => {
			updateCurrentConfig(currentConfig);
		});
	};

	setGeneralError = (message: string | false) => {
		this.setState({ generalError: message });
	};

	getLoginProps = () => {
		const loginProps: LoginProps = {
			integrationUrl: this.state.integrationApiUrl || "",
			gh_client_id: process.env.REACT_APP_GH_CLIENT_ID,
			getUserToken: this.getUserToken,
			setGeneralError: this.setGeneralError,
			setCorporateUserOrgs: this.setCorporateUserOrgs,
			setCurrentComponent: this.setCurrentComponent,
			changeCorporateUserOrg: (user_org: string, refresh: boolean) => {
				this.setState({ refresh_repos_on_org_change: refresh }, () => {
					this.changeCorporateUserOrg(user_org);
				});
			},
		};
		return loginProps;
	};

	getErrHistoryProps = () => {
		const historyProps: ErrHistoryProps = {
			integrationUrl: this.state.integrationApiUrl || "",
			currentConfig: this.state.currentConfig || ({} as RepoLink),
			setCurrentComponent: this.setCurrentComponent,
			setGeneralError: this.setGeneralError,
		};
		return historyProps;
	};

	setRefreshingTableByOrgChange = (refreshing: boolean, callback?: () => void) => {
		this.setState({ refreshing_repos_org_change: refreshing }, callback);
	};

	getLinkTableProps = () => {
		const props: LinkTableProps = {
			integrationDomain: this.state.integrationApiUrl || "",
			kanbanizeDomain: this.state.kanbanizeDomain || "",
			userToken: this.state.userToken || "",
			listConfig: this.state.listConfig,
			boards: this.state.boards,
			refreshing: this.state.refreshing_repos_org_change,
			setListConfig: this.setListConfig,
			setRefreshingTableByOrgChange: this.setRefreshingTableByOrgChange,
			setCurrentComponent: this.setCurrentComponent,
			setRepositories: this.setRepositories,
			setBoards: this.setBoards,
			setCurrentConfig: this.setCurrentConfig,
			setGeneralError: this.setGeneralError,
		};
		return props;
	};

	getLayoutProps = () => {
		const props: LayoutProps = {
			generalError: this.state.generalError,
			integrationUrl: this.state.integrationApiUrl || "",
			userToken: this.state.userToken || "",
			corporateUserOrgs: this.state.corporateUserOrgs,
			currentUserOrg: this.state.currentUserOrg,
			showOrgSelect: this.state.current_component === "LinkTable",
			setUserToken: this.setUserToken,
			setUserOrg: this.changeCorporateUserOrg,
			logOut: this.logOut,
			setGeneralError: this.setGeneralError,
		};
		return props;
	};

	getHomeProps = () => {
		let oauthUrl = "";
		const integration = process.env.REACT_APP_INTEGRATION_SERVICE;
		if (!integration) throw new Error("Error reading environment (INTEGRATION_SERVICE)");
		oauthUrl = `${process.env.REACT_APP_INTEGRATION_URL}/api/auth/${integration.toLowerCase()}/oauth`;
		const props: HomeProps = {
			oauthUrl,
		};
		return props;
	};

	getLinkFormProps = () => {
		const props: LinkFormProps = {
			integrationUrl: this.state.integrationApiUrl || "",
			editingReponame: this.state.currentlyEditingRepoName || "",
			userToken: this.state.userToken || "",
			currentConfig: this.state.currentConfig || ({} as RepoLink),
			listConfig: this.state.listConfig,
			repositories: this.state.repositories,
			boards: this.state.boards,
			boardDataMap: this.state.boardDataMap,
			repoDataMap: this.state.repoDataMap,
			setCurrentComponent: this.setCurrentComponent,
			setGeneralError: this.setGeneralError,
			addRepoDataMap: this.addRepoDataMap,
			addBoardDataMap: this.addBoardDataMap,
			setBoards: this.setBoards,
			setRepositories: this.setRepositories,
		};
		return props;
	};

	getUserToken = async (kanbanizeDomain: string, apiKey: string) => {
		try {
			const userName = this.state.userName;
			if (!kanbanizeDomain || !apiKey || !userName) return;
			const credentials: KanbanizeCredentials = {
				kanbanize_domain: kanbanizeDomain,
				api_key: apiKey
			};
			let loginUrl = "";
			if (process.env.REACT_APP_INTEGRATION_SERVICE !== "GitHub") {
				loginUrl = `${this.state.integrationApiUrl}/api/auth/login`;
			} else {
				loginUrl = `${this.state.integrationApiUrl}/api/login`;
			}
			const jsonData: KanbanizeTokenObj = await doPost(loginUrl, credentials, "");
			this.setState({ userToken: jsonData.token, kanbanizeDomain: kanbanizeDomain });
		} catch (e: any) {
			this.setState({ generalError: e.message });
		}
	};

	render(): React.ReactNode {
		const theme = createTheme({
			typography: {
				fontFamily: ['"Segoe UI"', '"Helvetica Neue"', "Arial", "sans-serif", '"Segoe UI Emoji"', '"Segoe UI Symbol"'].join(
					",",
				),
				fontSize: 16,
			},
		});
		return (
			<Container>
				<ThemeProvider theme={theme}>
					<Routes>
						<Route path="/" element={<Layout {...this.getLayoutProps()} />}>
							<Route index element={<Navigate to="/home" replace={true} />} />
							<Route
								path="/home"
								element={
									(this.state.userName && <Navigate to="/login" replace={true} />) || <Home {...this.getHomeProps()} />
								}
							/>
							<Route
								path="/login"
								element={
									((this.state.userToken || this.state.corporateUserOrgs.length) && (
										<Navigate to="/currentLinks" replace={true} />
									)) ||
									(this.state.userName && <Login {...this.getLoginProps()} />) || <Navigate to="/home" replace={true} />
								}
							/>
							<Route
								path="/currentLinks"
								element={
									((this.state.userToken || this.state.corporateUserOrgs.length) && (
										<LinkTable {...this.getLinkTableProps()} />
										// <Test />
									)) || <Navigate to="/login" replace={true} />
								}
							/>
							<Route
								path="/linkForm"
								element={
									((this.state.userToken || this.state.corporateUserOrgs.length) && (
										<LinkForm {...this.getLinkFormProps()} />
									)) || <Navigate to="/login" replace={true} />
								}
							/>
							<Route
								path="/errHistory"
								element={
									((this.state.userToken || this.state.corporateUserOrgs.length) && (
										<ErrorHistory {...this.getErrHistoryProps()} />
										// <Test />
									)) || <Navigate to="/login" replace={true} />
								}
							></Route>
						</Route>
					</Routes>
				</ThemeProvider>
			</Container>
		);
	}
}
